/* Hooks TanStack Query da gestão de staff (ADR-0709). As mutações invalidam a lista
   `['staff-management']` (distinta da sessão `['staff','session']`). */
import type {
  BulkStaffStatusBody,
  CreateStaffBody,
  StaffRole,
  StaffStatus,
} from '@planuze/admin-contracts';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  bulkStaffStatus,
  createStaff,
  listStaff,
  resetStaffPassword,
  updateStaffRole,
  updateStaffStatus,
} from '../lib/staff-api.js';

const STAFF_KEY = ['staff-management'] as const;

/* Lista paginada por cursor (ADR-0709): `useInfiniteQuery` + "Carregar mais" na página,
   espelhando `useCompaniesList`. As mutações invalidam `STAFF_KEY` (reseta as páginas). */
export function useStaffList() {
  return useInfiniteQuery({
    queryKey: STAFF_KEY,
    queryFn: ({ pageParam }) => listStaff({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

function useInvalidateStaff(): () => void {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: STAFF_KEY });
  };
}

export function useCreateStaff() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: (body: CreateStaffBody) => createStaff(body),
    onSuccess: invalidate,
  });
}

export function useUpdateStaffRole() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: (input: { readonly id: string; readonly role: StaffRole }) =>
      updateStaffRole(input.id, input.role),
    onSuccess: invalidate,
  });
}

export function useUpdateStaffStatus() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: (input: { readonly id: string; readonly status: StaffStatus }) =>
      updateStaffStatus(input.id, input.status),
    onSuccess: invalidate,
  });
}

export function useBulkStaffStatus() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: (body: BulkStaffStatusBody) => bulkStaffStatus(body),
    onSuccess: invalidate,
  });
}

export function useResetStaffPassword() {
  return useMutation({
    mutationFn: (input: { readonly id: string; readonly password: string }) =>
      resetStaffPassword(input.id, input.password),
  });
}
